import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, ClipboardTextIcon as ClipboardText } from "@phosphor-icons/react";
import { toHex, badgeStyle } from "@/lib/adlColors";

const newChkId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

function ColorPicker({ value, onChange, testid }) {
  return (
    <input type="color" value={toHex(value)} onChange={(e) => onChange(e.target.value)}
      className="h-9 w-10 rounded-sm border border-border cursor-pointer shrink-0" data-testid={testid} title="Colore" />
  );
}

function OptionColorEditor({ label, hint, items, onChange, testid, systemValues = [] }) {
  const list = items || [];
  const add = () => onChange([...list, { value: "", label: "", color: "#2563eb" }]);
  const upd = (i, k, v) => { const a = [...list]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="border border-border rounded-sm p-4 md:col-span-2" data-testid={`adl-cfg-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <div><Label className="text-xs uppercase tracking-wider">{label}</Label>{hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}</div>
        <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={add} data-testid={`adl-cfg-${testid}-add`}><Plus size={14} /> Aggiungi voce</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna voce.</p> : (
        <div className="space-y-2">
          {list.map((it, i) => {
            const isSystem = systemValues.includes(it.value);
            return (
              <div key={i} className="flex flex-wrap items-center gap-3 border border-border rounded-sm p-2.5 bg-secondary/20" data-testid={`adl-cfg-${testid}-row-${i}`}>
                <span className="inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-xs shrink-0" style={badgeStyle(it.color)} data-testid={`adl-cfg-${testid}-preview-${i}`}>
                  {it.label || "Etichetta"}
                </span>
                <Input value={it.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Etichetta visibile" className="rounded-sm h-9 flex-1 min-w-[150px] bg-white" data-testid={`adl-cfg-${testid}-label-${i}`} />
                <ColorPicker value={it.color} onChange={(c) => upd(i, "color", c)} testid={`adl-cfg-${testid}-color-${i}`} />
                {isSystem
                  ? <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-0.5 shrink-0" title="Voce di sistema, non eliminabile">Sistema</span>
                  : <button onClick={() => del(i)} className="text-muted-foreground hover:text-destructive shrink-0" title="Elimina" data-testid={`adl-cfg-${testid}-del-${i}`}><Trash size={15} /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ListEditor({ label, hint, items, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Voce già presente"); return; }
    onChange([...items, v]); setVal("");
  };
  return (
    <div className="border border-border rounded-sm p-4" data-testid={`adl-cfg-${testid}`}>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      <div className="flex gap-2 my-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Aggiungi voce…" className="rounded-sm" data-testid={`adl-cfg-${testid}-input`} />
        <Button onClick={add} className="rounded-sm gap-1 shrink-0" data-testid={`adl-cfg-${testid}-add`}><Plus size={15} /> Aggiungi</Button>
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna voce.</p> : (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span key={it} className="inline-flex items-center gap-2 rounded-sm border border-border bg-secondary/50 px-2.5 py-1 text-sm" data-testid={`adl-cfg-${testid}-item-${it}`}>
              {it}
              <button onClick={() => onChange(items.filter((x) => x !== it))} className="text-muted-foreground hover:text-destructive" data-testid={`adl-cfg-${testid}-del-${it}`}><Trash size={13} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ label, hint, items, cats, onChange, testid }) {
  const list = items || [];
  const add = () => onChange([...list, { titolo: "", categoria: "", checklist: [] }]);
  const upd = (i, k, v) => { const a = [...list]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  const del = (i) => onChange(list.filter((_, j) => j !== i));
  const addChk = (i) => { const a = [...list]; a[i] = { ...a[i], checklist: [...(a[i].checklist || []), { id: newChkId(), testo: "", fatto: false }] }; onChange(a); };
  const updChk = (i, j, testo) => { const a = [...list]; const c = [...(a[i].checklist || [])]; c[j] = { ...c[j], testo }; a[i] = { ...a[i], checklist: c }; onChange(a); };
  const delChk = (i, j) => { const a = [...list]; a[i] = { ...a[i], checklist: (a[i].checklist || []).filter((_, k) => k !== j) }; onChange(a); };
  return (
    <div className="border border-border rounded-sm p-4 md:col-span-2" data-testid={`adl-cfg-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <div><Label className="text-xs uppercase tracking-wider">{label}</Label>{hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}</div>
        <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={add} data-testid={`adl-cfg-${testid}-add`}><Plus size={14} /> Aggiungi template</Button>
      </div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground">Nessun template.</p> : (
        <div className="space-y-3">
          {list.map((t, i) => (
            <div key={i} className="border border-border rounded-sm p-3 bg-secondary/20" data-testid={`adl-cfg-${testid}-row-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Input value={t.titolo || ""} onChange={(e) => upd(i, "titolo", e.target.value)} placeholder="Titolo template" className="rounded-sm h-9 flex-1 min-w-[180px] bg-white" data-testid={`adl-cfg-${testid}-titolo-${i}`} />
                <Input list={`cats-${testid}`} value={t.categoria || ""} onChange={(e) => upd(i, "categoria", e.target.value)} placeholder="Categoria" className="rounded-sm h-9 w-48 bg-white" />
                <button onClick={() => del(i)} className="text-muted-foreground hover:text-destructive" title="Elimina template" data-testid={`adl-cfg-${testid}-del-${i}`}><Trash size={15} /></button>
              </div>
              <div className="mt-2 pl-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Voci da flaggare</span>
                  <Button size="sm" variant="ghost" className="h-7 rounded-sm gap-1 text-xs" onClick={() => addChk(i)} data-testid={`adl-cfg-${testid}-chk-add-${i}`}><Plus size={13} /> Aggiungi voce</Button>
                </div>
                {(t.checklist || []).length === 0 ? <p className="text-xs text-muted-foreground">Nessuna voce.</p> : (
                  <div className="space-y-1.5">
                    {(t.checklist || []).map((c, j) => (
                      <div key={c.id || j} className="flex items-center gap-2">
                        <span className="h-3.5 w-3.5 rounded-sm border border-muted-foreground/50 shrink-0" />
                        <Input value={c.testo || ""} onChange={(e) => updChk(i, j, e.target.value)} placeholder="Voce da flaggare" className="rounded-sm h-8 flex-1 bg-white" data-testid={`adl-cfg-${testid}-chk-${i}-${j}`} />
                        <button onClick={() => delChk(i, j)} className="text-muted-foreground hover:text-destructive" data-testid={`adl-cfg-${testid}-chk-del-${i}-${j}`}><Trash size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <datalist id={`cats-${testid}`}>{(cats || []).map((c) => <option key={c} value={c} />)}</datalist>
        </div>
      )}
    </div>
  );
}

export function ScadenziarioConfigSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin-deadlines/config").then(({ data }) => setCfg(data)).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel caricamento"); setCfg({ categorie_tecnologie: [], categorie_clienti: [], fornitori: [], norme: [], ambiti: [], evidenze: [], templates_tecnologie: [], templates_clienti: [], priorita: [], stati: [], visibilita: [] }); }); }, []);
  if (!cfg) return <Card className="rounded-sm border-border shadow-none p-6">Caricamento…</Card>;
  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin-deadlines/config", {
        categorie_tecnologie: cfg.categorie_tecnologie || [],
        categorie_clienti: cfg.categorie_clienti || [],
        fornitori: cfg.fornitori || [],
        norme: cfg.norme || [], ambiti: cfg.ambiti || [], evidenze: cfg.evidenze || [],
        templates_tecnologie: cfg.templates_tecnologie || [],
        templates_clienti: cfg.templates_clienti || [],
        priorita: cfg.priorita || [], stati: cfg.stati || [], visibilita: cfg.visibilita || [],
      });
      setCfg(data); toast.success("Configurazione Scadenziario salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="scadenziario-config-section">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><ClipboardText size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Scadenziario — elenchi e template</h3>
            <p className="text-xs text-muted-foreground">Categorie, liste e modelli rapidi usati nei form delle scadenze (Tecnologie e Clienti)</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="adl-cfg-save">{saving ? "Salvataggio…" : "Salva"}</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <OptionColorEditor label="Priorità" hint="Opzioni e colore del menu Priorità nelle scadenze" items={cfg.priorita || []} onChange={(v) => upd("priorita", v)} testid="priorita" systemValues={["bassa", "media", "alta", "critica"]} />
        <OptionColorEditor label="Stato" hint="Opzioni e colore del menu Stato nelle scadenze" items={cfg.stati || []} onChange={(v) => upd("stati", v)} testid="stati" systemValues={["aperta", "in_lavorazione", "completata", "rinviata", "annullata"]} />
        <OptionColorEditor label="Visibilità" hint="Opzioni e colore del menu Visibilità nelle scadenze" items={cfg.visibilita || []} onChange={(v) => upd("visibilita", v)} testid="visibilita" />
        <ListEditor label="Categorie — Tecnologie" hint="Categorie disponibili nella pagina Scadenziario → Tecnologie" items={cfg.categorie_tecnologie || []} onChange={(v) => upd("categorie_tecnologie", v)} testid="cat-tec" />
        <ListEditor label="Categorie — Clienti" hint="Categorie disponibili nella pagina Scadenziario → Clienti" items={cfg.categorie_clienti || []} onChange={(v) => upd("categorie_clienti", v)} testid="cat-cli" />
        <ListEditor label="Fornitori ricorrenti" hint="Suggeriti nel campo Fornitore delle scadenze" items={cfg.fornitori || []} onChange={(v) => upd("fornitori", v)} testid="fornitori" />
        <ListEditor label="Norme ISO" items={cfg.norme || []} onChange={(v) => upd("norme", v)} testid="norme" />
        <ListEditor label="Ambiti ISO" items={cfg.ambiti || []} onChange={(v) => upd("ambiti", v)} testid="ambiti" />
        <ListEditor label="Evidenze" items={cfg.evidenze || []} onChange={(v) => upd("evidenze", v)} testid="evidenze" />
        <TemplateEditor label="Template — Tecnologie" hint="Modelli rapidi mostrati creando una scadenza nella pagina Tecnologie" items={cfg.templates_tecnologie || []} cats={cfg.categorie_tecnologie || []} onChange={(v) => upd("templates_tecnologie", v)} testid="tpl-tec" />
        <TemplateEditor label="Template — Clienti" hint="Modelli rapidi mostrati creando una scadenza nella pagina Clienti" items={cfg.templates_clienti || []} cats={cfg.categorie_clienti || []} onChange={(v) => upd("templates_clienti", v)} testid="tpl-cli" />
      </div>
      <div className="mt-4"><Button onClick={save} disabled={saving} className="rounded-sm" data-testid="adl-cfg-save-2">{saving ? "Salvataggio…" : "Salva"}</Button></div>
    </Card>
  );
}
